import { Refine } from '@refinedev/core';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import routerProvider from '@refinedev/react-router-v6';

import { authProvider } from './providers/auth-provider';
import { dataProvider } from './providers/data-provider';
import { AdminLayout } from './components/layout/AdminLayout';
import { LoginPage } from './pages/auth/LoginPage';
import { DashboardPage } from './pages/dashboard';
import { LaunchCommandCenterPage } from './pages/launch-command-center';
import { GrowthPage } from './pages/growth';
import { UserListPage } from './pages/users/list';
import { UserShowPage } from './pages/users/show';
import { SupportLookupPage } from './pages/support/lookup';
import { WaitlistPage } from './pages/waitlist';
import { BetaApplicationsPage } from './pages/beta';
import { FeedbackPage } from './pages/feedback';
import { AuditLogPage } from './pages/audit';
import { ReportsPage } from './pages/reports';
import { VerificationQueuePage } from './pages/verification';
import VerificationWhitelistPage from './pages/verification/whitelist';
import { PhotoModerationPage } from './pages/moderation/photos';
import { SettingsPage } from './pages/settings';
import { SecurityPage } from './pages/security';
import { AdminsListPage } from './pages/security/admins';
import { RolesListPage } from './pages/security/roles';
import { PermissionsCatalogPage } from './pages/security/permissions';
import { RequirePermission } from './components/RequirePermission';
import { PromotionsListPage } from './pages/promotions/list';
import { PromotionCreatePage } from './pages/promotions/create';
import { PromotionShowPage } from './pages/promotions/show';
import { PromotionEditPage } from './pages/promotions/edit';
import { ReferralsListPage } from './pages/referrals/list';
import { ReferralCampaignCreatePage } from './pages/referrals/create';
import { ReferralCampaignShowPage } from './pages/referrals/show';
import { QuestionsListPage } from './pages/questions/list';
import { QuestionCreatePage } from './pages/questions/create';
import { QuestionEditPage } from './pages/questions/edit';
import { BulkGrantPage } from './pages/coupons/bulk-grant';
import { UserSegmentsPage } from './pages/segments/list';
import { AcquisitionPage } from './pages/acquisition';
import { CampaignLinkBuilderPage } from './pages/campaign-links';
import { AgentChatPage } from './pages/agent';
import { AgentSharePage } from './pages/agent/share';
import { SystemHealthPage } from './pages/system-health';
import { NotificationsPage } from './pages/notifications';
import { SupportInboxPage } from './pages/support-inbox';
import { SupportThreadPage } from './pages/support-inbox/thread';
import { AppExperiencePage } from './pages/app-experience';
import MatchesPage from './pages/matches';
import MatchDetailPage from './pages/matches/$matchId';
import MatchComparePage from './pages/matches/compare';
import MatchNewPage from './pages/matches/new';
import MatchScoringDiagnosticPage from './pages/matches/scoring-diagnostic';
import { TooltipProvider } from './components/ui/Tooltip';
import { Toaster } from './components/ui/Toaster';
import { AdminPermissionsProvider } from './contexts/AdminPermissionsContext';

const API_URL = import.meta.env.VITE_API_URL || '';

function App() {
  return (
    <BrowserRouter>
      <TooltipProvider>
      <Refine
        routerProvider={routerProvider}
        authProvider={authProvider}
        dataProvider={dataProvider(API_URL)}
        resources={[
          {
            name: 'dashboard',
            list: '/dashboard',
            meta: {
              label: 'Dashboard',
              icon: 'LayoutDashboard',
            },
          },
          {
            name: 'users',
            list: '/users',
            show: '/users/:id',
            meta: {
              label: 'Users',
              icon: 'Users',
            },
          },
          {
            name: 'waitlist',
            list: '/waitlist',
            meta: {
              label: 'Waitlist',
              icon: 'Mail',
            },
          },
          {
            name: 'beta-applications',
            list: '/beta-applications',
            meta: {
              label: 'Beta Applications',
              icon: 'Rocket',
            },
          },
          {
            name: 'support',
            list: '/support',
            meta: {
              label: 'Support',
              icon: 'HeadphonesIcon',
            },
          },
          {
            name: 'feedback',
            list: '/feedback',
            meta: {
              label: 'Feedback',
              icon: 'MessageSquare',
            },
          },
          {
            name: 'verification',
            list: '/verification',
            meta: {
              label: 'Verification',
              icon: 'ShieldCheck',
            },
          },
          {
            name: 'verification-whitelist',
            list: '/verification/whitelist',
            meta: {
              label: 'Whitelist',
              icon: 'ShieldCheck',
              parent: 'verification',
            },
          },
          {
            name: 'moderation',
            list: '/moderation',
            meta: {
              label: 'Photo Moderation',
              icon: 'ImageIcon',
            },
          },
          {
            name: 'reports',
            list: '/reports',
            meta: {
              label: 'Reports',
              icon: 'AlertTriangle',
            },
          },
          {
            name: 'audit',
            list: '/audit',
            meta: {
              label: 'Audit Log',
              icon: 'ScrollText',
            },
          },
          {
            name: 'promotions',
            list: '/promotions',
            show: '/promotions/:id',
            create: '/promotions/new',
            edit: '/promotions/:id/edit',
            meta: { label: 'Promotions', icon: 'Gift' },
          },
          {
            name: 'coupons-bulk-grant',
            list: '/coupons/bulk-grant',
            meta: { label: 'Bulk Grant Coupons', icon: 'UserPlus', parent: 'promotions' },
          },
          {
            name: 'referrals',
            list: '/referrals',
            show: '/referrals/:id',
            create: '/referrals/new',
            meta: { label: 'Referrals', icon: 'Share2' },
          },
          {
            name: 'questions',
            list: '/questions',
            create: '/questions/new',
            edit: '/questions/:id/edit',
            meta: { label: 'Onboarding Questions', icon: 'ListChecks' },
          },
          {
            name: 'user-segments',
            list: '/segments',
            meta: { label: 'BETA Cohorts', icon: 'Layers' },
          },
        ]}
        options={{
          syncWithLocation: true,
          warnWhenUnsavedChanges: true,
          // 🔴 NO OUTBOUND BEACON FROM AN AUTHENTICATED ADMIN PAGE (2026-10-25).
          // `@refinedev/core` fires vendor telemetry to telemetry.refine.dev from
          // a React effect when <Refine> mounts, once per full page load. It sends
          // the configured providers, the package version, a resource count and a
          // projectId; the request's headers add the operator's IP, hostname,
          // browser and OS. No member data — but this is the signed-in admin
          // surface of a dating platform, and it is not a destination we chose.
          //
          // It was ALREADY blocked, by accident rather than by design: the panel's
          // CSP is `default-src 'none'` with an enumerated `img-src`, and refine's
          // transport prefers an Image beacon, so Chrome refuses it. Verified in
          // the live PREDEV panel on 2026-10-25 — a securitypolicyviolation event
          // with blockedURI telemetry.refine.dev, effectiveDirective `img-src`,
          // disposition `enforce`. The fetch fallback is unreachable (Image is
          // always defined in a browser) and `connect-src` excludes the host too.
          //
          // Relying on that is fragile: one wildcard added to `img-src`, or a
          // vendor transport change, silently re-opens it — and the admin CSP
          // carries no report-uri, so nobody would learn. Stop it at the source
          // as well, so the beacon is off by configuration and blocked by policy.
          // Pinned by tests/unit/lint/admin-disables-vendor-telemetry.test.ts.
          disableTelemetry: true,
        }}
      >
        <AdminPermissionsProvider>
        <Routes>
          <Route path="/login" element={<LoginPage />} />
          <Route element={<AdminLayout />}>
            <Route index element={<Navigate to="/dashboard" replace />} />
            <Route path="/dashboard" element={<DashboardPage />} />
            <Route path="/launch-command-center" element={<LaunchCommandCenterPage />} />
            <Route path="/growth" element={<GrowthPage />} />
            <Route path="/users" element={<UserListPage />} />
            <Route path="/users/:id" element={<UserShowPage />} />
            <Route path="/waitlist" element={<WaitlistPage />} />
            <Route
              path="/beta-applications"
              element={
                <RequirePermission permission="growth.waitlist.read">
                  <BetaApplicationsPage />
                </RequirePermission>
              }
            />
            <Route path="/support" element={<SupportLookupPage />} />
            {/* The sidebar has hidden these two entries behind `feedback.read`
                and `moderation.verification.review` since migration 210, but the
                ROUTES were open: a typed URL rendered the page for any admin.
                It did not matter while the backend enforced neither key — it
                returned the rows. Both endpoints enforce them from 2026-09-20
                (backend/src/routes/feedback.ts, routes/verification.ts), so an
                unwrapped route would now paint a page and then 403 into it.
                Wrapped, the SPA says the same thing as the sidebar and the API. */}
            <Route
              path="/feedback"
              element={
                <RequirePermission permission="feedback.read">
                  <FeedbackPage />
                </RequirePermission>
              }
            />
            <Route
              path="/verification"
              element={
                <RequirePermission permission="moderation.verification.review">
                  <VerificationQueuePage />
                </RequirePermission>
              }
            />
            <Route path="/verification/whitelist" element={<VerificationWhitelistPage />} />
            <Route path="/moderation" element={<PhotoModerationPage />} />
            <Route path="/reports" element={<ReportsPage />} />
            <Route
              path="/audit"
              element={
                <RequirePermission permission="audit.read_own">
                  <AuditLogPage />
                </RequirePermission>
              }
            />
            <Route
              path="/settings"
              element={
                <RequirePermission permission="settings.read">
                  <SettingsPage />
                </RequirePermission>
              }
            />
            <Route
              path="/security"
              element={
                <RequirePermission permission="admin.list">
                  <SecurityPage />
                </RequirePermission>
              }
            />
            <Route
              path="/security/admins"
              element={
                <RequirePermission permission="admin.list">
                  <AdminsListPage />
                </RequirePermission>
              }
            />
            <Route path="/promotions" element={<PromotionsListPage />} />
            <Route path="/promotions/new" element={<PromotionCreatePage />} />
            <Route path="/promotions/:id" element={<PromotionShowPage />} />
            <Route path="/promotions/:id/edit" element={<PromotionEditPage />} />
            <Route
              path="/referrals"
              element={
                <RequirePermission permission="growth.referrals.read">
                  <ReferralsListPage />
                </RequirePermission>
              }
            />
            <Route
              path="/referrals/new"
              element={
                <RequirePermission permission="growth.referrals.write">
                  <ReferralCampaignCreatePage />
                </RequirePermission>
              }
            />
            <Route
              path="/referrals/:id"
              element={
                <RequirePermission permission="growth.referrals.read">
                  <ReferralCampaignShowPage />
                </RequirePermission>
              }
            />
            <Route path="/questions" element={<QuestionsListPage />} />
            <Route path="/questions/new" element={<QuestionCreatePage />} />
            <Route path="/questions/:id/edit" element={<QuestionEditPage />} />
            <Route path="/coupons/bulk-grant" element={<BulkGrantPage />} />
            <Route path="/segments" element={<UserSegmentsPage />} />
            {/* T2 PR-2 (2026-05-28): analytics tabs — gated on permissions seeded by mig 212. */}
            <Route
              path="/acquisition"
              element={
                <RequirePermission permission="analytics.acquisition.read">
                  <AcquisitionPage />
                </RequirePermission>
              }
            />
            <Route
              path="/system-health"
              element={
                <RequirePermission permission="analytics.system_health.read">
                  <SystemHealthPage />
                </RequirePermission>
              }
            />
            {/* Notification rails v2 (spec 2026-09-22 §4.5, task 5.2). Gated on
                `notifications.read`, seeded by migration 343. The sidebar hides
                the entry on the same key; the route is wrapped too, because a
                typed URL must say the same thing as the sidebar and the API. */}
            <Route
              path="/notifications"
              element={
                <RequirePermission permission="notifications.read">
                  <NotificationsPage />
                </RequirePermission>
              }
            />
            {/* T3 PR-4 (2026-05-29): role CRUD + permission catalog. */}
            <Route
              path="/security/roles"
              element={
                <RequirePermission permission="admin.list">
                  <RolesListPage />
                </RequirePermission>
              }
            />
            <Route
              path="/security/permissions"
              element={
                <RequirePermission permission="admin.list">
                  <PermissionsCatalogPage />
                </RequirePermission>
              }
            />
            {/* Matches surface — Topic 1 PR-3.
                Added at end-of-list to minimize merge conflict with concurrent
                frontend PRs (T2 PR-2, T4 PR-2). Permission gates enforce per
                spec §7.4; sidebar visibility gated on `matches.list`. */}
            <Route
              path="/matches"
              element={
                <RequirePermission permission="matches.list">
                  <MatchesPage />
                </RequirePermission>
              }
            />
            <Route
              path="/matches/compare"
              element={
                <RequirePermission permission="matches.compare">
                  <MatchComparePage />
                </RequirePermission>
              }
            />
            <Route
              path="/matches/new"
              element={
                <RequirePermission permission="matches.create">
                  <MatchNewPage />
                </RequirePermission>
              }
            />
            <Route
              path="/matches/:matchId"
              element={
                <RequirePermission permission="matches.read">
                  <MatchDetailPage />
                </RequirePermission>
              }
            />
            <Route
              path="/matches/:matchId/scoring-diagnostic"
              element={
                <RequirePermission permission="matches.read">
                  <MatchScoringDiagnosticPage />
                </RequirePermission>
              }
            />
            {/* Campaign Links builder (2026-08-11). Reuses `growth.referrals.read`
                rather than minting a key: the audience is exactly the people who
                already mint /r/ links, and a new key needs a migration against a
                database stack near its 200-CFN-output cap. Appended at the end of
                the list per the convention noted above. */}
            <Route
              path="/campaign-links"
              element={
                <RequirePermission permission="growth.referrals.read">
                  <CampaignLinkBuilderPage />
                </RequirePermission>
              }
            />
            {/* Analytics Agent (2026-08-21, spec 2026-08-20-admin-agent-catalogue-design).

                🔴 THE CHAT ROUTE IS WRAPPED IN `agent.chat` AND THE SHARE ROUTE
                IS NOT, and neither half is an oversight.

                `agent.chat` is the TRANSPORT key: holding it is what lets a
                request reach the agent at all, and the backend conjuncts it
                with each metric's page keys on every tool call. Gating the chat
                route on it keeps the client and the server saying the same
                thing.

                `/agent/a/:token` carries an OPAQUE TOKEN and nothing else. The
                browser cannot know which page keys the underlying metric needs
                — there is no client-side catalogue in `frontend/admin/src` —
                so any wrapper here would be a guess, and a guessed key on the
                surface an operator looks at is weaker than the backend
                conjunction, in the permissive direction.
                `RequirePermission`'s array form is ANY-of (see its own doc
                comment), which would gate a two-key metric on EITHER key —
                weaker still. `getSharedAnswer` re-runs the metric under the
                CALLER's `AdminUser` and returns a `PermissionRedactedCell`
                naming the missing key, with no numeric payload of any kind.
                `tests/unit/lint/agent-chat-no-remote-render.test.ts` asserts
                the ABSENCE of a wrapper on this route, so the deletion is
                permanent.

                Both routes are additionally gated server-side by
                `verifyAdminAuth` under `/v1/admin/*`: a share is
                admin-to-admin, never public. */}
            <Route
              path="/agent"
              element={
                <RequirePermission permission="agent.chat">
                  <AgentChatPage />
                </RequirePermission>
              }
            />
            <Route path="/agent/a/:token" element={<AgentSharePage />} />

            {/* Support inbox (design 2026-09-26, section 6). Both routes gate on
                `support_inbox.read`, seeded by migration 349; the write, the
                restricted mailboxes and the erase are gated inside the pages by
                their own keys and again server-side. Appended at the END, and
                both patterns are in the two ADMIN_ROUTE_PATTERNS tables,
                specific before param. */}
            <Route
              path="/support-inbox"
              element={
                <RequirePermission permission="support_inbox.read">
                  <SupportInboxPage />
                </RequirePermission>
              }
            />
            <Route
              path="/support-inbox/:threadId"
              element={
                <RequirePermission permission="support_inbox.read">
                  <SupportThreadPage />
                </RequirePermission>
              }
            />

            {/* App Experience (spec 2026-10-23 §5): the intro video before
                sign-up and the Abby welcome. `experience.read` is seeded by
                migration 388 for super_admin, growth and marketing; publishing
                needs `experience.write`, checked inside the page and again
                server-side. Appended at the END; the pattern is in both
                ADMIN_ROUTE_PATTERNS tables. */}
            <Route
              path="/app-experience"
              element={
                <RequirePermission permission="experience.read">
                  <AppExperiencePage />
                </RequirePermission>
              }
            />
          </Route>
        </Routes>
        </AdminPermissionsProvider>
      </Refine>
      <Toaster />
      </TooltipProvider>
    </BrowserRouter>
  );
}

export default App;
